import { toolsSections } from "@/lib/guides/sections-tools";
import { gta5Sections } from "@/lib/guides/sections-gta5";
import { newPaidToolSections } from "@/lib/guides/sections-new-paid";
import { freeToolSections } from "@/lib/guides/sections-free";
import type { GuideId, GuideManifest } from "@/lib/guides/types";

export const guideManifests: Record<GuideId, GuideManifest> = {
  tools: {
    id: "tools",
    title: "Tool Guides",
    // Counted, not claimed: 18 established tool sections, the three tools that
    // left admin-only on this branch (Handling Diff, Resmon Report, Asset
    // Directory) and the eight free tools. The free tools' guides are short
    // how-tos that point at their `/free/**` pages for the full reference, so
    // the two never duplicate each other.
    subtitle:
      "Step-by-step walkthroughs for 29 Workshop tools — how each one works, what it accepts, and how to get the most out of it.",
    breadcrumb: "Tool Guides",
    sections: [...toolsSections, ...newPaidToolSections, ...freeToolSections],
  },
  gta5: {
    id: "gta5",
    title: "GTA Knowledge",
    subtitle:
      "File formats, vehicle meta files, FiveM resource structure, EUP clothing slots, texture workflows, the 32 Siren Script, and modkit ID fixes — everything you need to work with GTA5 assets on FiveM.",
    breadcrumb: "GTA Knowledge",
    sections: gta5Sections,
  },
};

export function getGuideManifest(guideId: string): GuideManifest | null {
  if (guideId !== "tools" && guideId !== "gta5") return null;
  return guideManifests[guideId];
}

/**
 * Strip a route down to the form `toolHref` is written in.
 *
 * Query strings and hashes are how a tool link actually arrives — a header
 * reading `usePathname()` gets a clean path, but a caller passing
 * `window.location.pathname + search` or a route with a trailing slash is not
 * doing anything unreasonable, and the alternative to normalising is a link that
 * silently stops rendering. Lower-cased because every `toolHref` in the
 * manifests is lower-case and Next routes are matched case-insensitively in
 * practice.
 */
function normalizeToolHref(href: string): string {
  const withoutQuery = href.split(/[?#]/)[0].trim().toLowerCase();
  if (withoutQuery === "" || withoutQuery === "/") return withoutQuery;
  return withoutQuery.endsWith("/") ? withoutQuery.slice(0, -1) : withoutQuery;
}

/**
 * The guide section that documents the tool at `href`, or `null`.
 *
 * `GuideSectionDef.toolHref` has always pointed guide → tool. Nothing pointed
 * the other way, which is why 1,590 lines of written tutorial ship and no tool
 * in the product links to the page that explains it. This is that arrow
 * inverted, once, over the manifests already loaded here — no second table to
 * keep in step, and no new content.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE `null` BRANCH IS A RENDERING REQUIREMENT, NOT A NON-THROW
 *
 * Four mounted walkthrough tours have no guide section at all —
 * `/blinklab/carcols`, `/ink-lab`, `/livery-magic` and the plugin-hub route —
 * so this returns `null` for every one of them and the caller must render **no
 * link element**. Not a link to `/guides`, not a disabled chip: a "How this
 * works" that lands on an index of eighteen other tools is worse than no link,
 * because the reader has already spent the click. `ToolGuideLink` in this
 * directory is that branch written once; `manifests.test.ts` pins the four.
 *
 * Matching is exact after normalisation, deliberately. A prefix match would make
 * `/blinklab/carcols` resolve to the `/blinklab` guide, and that guide
 * describes a different screen — which is precisely the "four named content
 * gaps" the census turns into "sixteen tools silently mislabelled".
 *
 * Both manifests are searched rather than only `tools`, because a knowledge
 * guide may carry a `toolHref` too and a reader standing in a tool does not
 * care which of our two guide sets happens to explain it. Where both name the
 * same route, the first registered wins — stable, because `guideManifests` is a
 * literal.
 *
 * The cost worth knowing before importing this: this module pulls both section
 * files, and `sections-tools.tsx` is 1,590 lines of JSX. Call it from a header
 * that is already a server component, or from a page, rather than dragging the
 * guide content into a client island that never renders it.
 */
export function guideHrefForTool(href: string): string | null {
  if (typeof href !== "string") return null;
  const wanted = normalizeToolHref(href);
  if (wanted === "") return null;

  for (const manifest of Object.values(guideManifests)) {
    for (const section of manifest.sections) {
      if (!section.toolHref) continue;
      if (normalizeToolHref(section.toolHref) === wanted) {
        return `/guides/${manifest.id}/${section.id}`;
      }
    }
  }
  return null;
}
